import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';

export default function ChangePassword() {
    const { token } = useAuth();
    const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMsg(null);
        if (!token) { setMsg({ type: 'error', text: 'Please login first.' }); return; }
        
        if (form.newPassword !== form.confirmPassword) {
            setMsg({ type: 'error', text: 'New password and confirm password do not match.' });
            return;
        }
        
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(form.newPassword)) {
            setMsg({ 
                type: 'error', 
                text: 'New password must be 8+ chars with uppercase, lowercase, number, and special character.' 
            });
            return;
        }

        setLoading(true);
        const res = await api.changePassword(token, { oldPassword: form.oldPassword, newPassword: form.newPassword });
        setLoading(false);
        if (res.message === 'Password changed successfully') {
            setMsg({ type: 'success', text: 'Password changed successfully! Keep it safe.' });
            setForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } else {
            setMsg({ type: 'error', text: res.message || 'Failed to change password.' });
        }
    };

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-[#05144b]">Security Settings</h2>
                <p className="text-[#05144b]/70 font-medium tracking-tight uppercase text-[10px]">Protect your account by ensuring you use a strong, unique password.</p>
            </div>

            {msg && (
                <div className={`p-4 rounded-2xl mb-6 flex items-center gap-3 font-bold border ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'}`}></i>
                    {msg.text}
                </div>
            )}

            <div className="bg-white rounded-[2rem] border border-[#05144b]/10 shadow-sm p-8 lg:p-10 max-w-2xl">
                <div className="flex items-center gap-4 mb-8 pb-4 border-b border-[#05144b]/5">
                    <div className="w-10 h-10 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center font-black">
                        <i className="fa-solid fa-shield-halved"></i>
                    </div>
                    <h4 className="text-xl font-bold text-[#05144b] uppercase italic tracking-tighter">Password Update</h4>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="form-group space-y-2">
                        <label className="text-[10px] font-black text-[#05144b]/40 uppercase tracking-widest ml-1 italic font-serif">Current Password</label>
                        <input 
                            type="password" 
                            className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl focus:ring-4 focus:ring-[#05144b]/5 outline-none transition-all font-black text-[#05144b] tracking-widest" 
                            name="oldPassword" 
                            value={form.oldPassword} 
                            onChange={handleChange} 
                            required 
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-[#05144b]/5">
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-[#05144b]/40 uppercase tracking-widest ml-1 italic font-serif">New Password</label>
                            <input 
                                type="password" 
                                className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl focus:ring-4 focus:ring-[#05144b]/5 outline-none transition-all font-black text-[#05144b] tracking-widest" 
                                name="newPassword" 
                                value={form.newPassword} 
                                onChange={handleChange} 
                                required 
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="form-group space-y-2">
                            <label className="text-[10px] font-black text-[#05144b]/40 uppercase tracking-widest ml-1 italic font-serif">Confirm Identity</label>
                            <input 
                                type="password" 
                                className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl focus:ring-4 focus:ring-[#05144b]/5 outline-none transition-all font-black text-[#05144b] tracking-widest" 
                                name="confirmPassword" 
                                value={form.confirmPassword} 
                                onChange={handleChange} 
                                required 
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <div className="bg-[#05144b]/5 p-4 rounded-xl border border-[#05144b]/10 border-dashed">
                        <p className="text-[10px] font-black text-[#05144b]/50 flex items-center gap-2 uppercase tracking-tight">
                            <i className="fa-solid fa-circle-info text-[#05144b]"></i>
                            Require 8+ chars (Upper, Lower, Number, Special)
                        </p>
                    </div>

                    <div className="mt-8">
                        <button className="w-full bg-[#05144b] text-[#ffbb00] border-2 border-[#ffbb00] py-5 rounded-2xl font-black text-[13px] uppercase tracking-[4px] shadow-xl hover:-translate-y-1 transition-all active:scale-95 disabled:opacity-50" type="submit" disabled={loading}>
                            {loading ? <><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Syncing security...</> : 'Launch security protocol'}
                        </button>
                    </div>
                </form>
            </div>
        </UserDashboardLayout>
    );
}


